import React, { useState, useEffect } from 'react';
import {
	Users,
	ChevronDown,
	AtSign,
	MessageSquare,
	Info
} from 'lucide-react';

const GroupsSection = ({
	instanceId,
	settings,
	onSettingsChange,
}) => {
	const [showGroupsSettings, setShowGroupsSettings] = useState(() => {
		return localStorage.getItem('showGroupsSettings') === 'true';
	});

	useEffect(() => {
		localStorage.setItem('showGroupsSettings', showGroupsSettings.toString());
	}, [showGroupsSettings]);

	const toggleGroupsSettings = () => {
		setShowGroupsSettings(!showGroupsSettings);
	};

	return (
		<div className="relative overflow-hidden bg-gradient-to-br from-violet-500/25 via-purple-500/20 to-fuchsia-500/25 rounded-xl border border-violet-500/50 shadow-lg hover:shadow-violet-500/25 transition-shadow duration-200">
			<button
				type="button"
				className="relative z-10 flex w-full items-center justify-between p-4 cursor-pointer bg-transparent text-left"
				onClick={toggleGroupsSettings}
				aria-expanded={showGroupsSettings}
			>
				<h3 className="font-medium flex items-center">
					<div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500/40 via-purple-500/30 to-fuchsia-500/40 shadow-lg border border-violet-500/60 mr-3">
						<Users className="h-4 w-4 text-violet-300" />
					</div>
					<span className="text-textPrimary">Grupos</span>
					{settings.GRUPOS_RESPONDER_MENCOES && (
						<span className="ml-2 px-2 py-0.5 text-xs bg-violet-500/30 text-violet-300 rounded-full">
							Ativo
						</span>
					)}
				</h3>
				<div
					className={`transform transition-transform duration-200 ${showGroupsSettings ? "rotate-180" : ""}`}
				>
					<ChevronDown
						className="h-5 w-5 text-textPrimary"
						aria-hidden="true"
					/>
				</div>
			</button>

			<div
				className={`transition-all duration-300 ease-in-out ${showGroupsSettings ? "max-h-[800px] opacity-100 overflow-y-auto" : "max-h-0 opacity-0 overflow-hidden"}`}
			>
				<div className="px-4 pb-4">
					<div className="mb-4 p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg">
						<div className="flex items-start gap-2">
							<Info className="h-5 w-5 text-violet-400 mt-0.5 flex-shrink-0" />
							<div>
								<h4 className="text-textPrimary font-semibold text-sm mb-1">
									Como Funciona
								</h4>
								<p className="text-xs text-textSecondary">
									O bot responde apenas quando for mencionado diretamente no grupo,
									seja pelo numero do WhatsApp ou por uma palavra-chave configuravel.
								</p>
							</div>
						</div>
					</div>

					<div className="relative z-10 bg-grayBase/80 rounded-lg p-5 space-y-5 shadow-inner border border-violet-500/20 backdrop-blur-sm">
						<label className="flex items-center justify-between p-4 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-violet-500/25 cursor-pointer">
							<div className="flex items-center gap-3">
								<div className="p-2 rounded-full bg-violet-500/20 border border-violet-500/40">
									<AtSign className="h-4 w-4 text-violet-300" />
								</div>
								<div>
									<span className="text-sm font-medium text-textPrimary">
										Responder Mencoes em Grupos
									</span>
									<p className="text-xs text-grayText">
										Responde quando o bot for mencionado
									</p>
								</div>
							</div>
							<div className="relative inline-flex items-center">
								<input
									type="checkbox"
									name="GRUPOS_RESPONDER_MENCOES"
									checked={settings.GRUPOS_RESPONDER_MENCOES || false}
									onChange={onSettingsChange}
									className="sr-only peer"
								/>
								<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-500/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
							</div>
						</label>

						{settings.GRUPOS_RESPONDER_MENCOES && (
							<div className="space-y-4">
								<label className="flex items-center justify-between p-4 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-violet-500/25 cursor-pointer">
									<div className="flex items-center gap-3">
										<div className="p-2 rounded-full bg-emerald-500/20 border border-emerald-500/40">
											<AtSign className="h-4 w-4 text-emerald-300" />
										</div>
										<div>
											<span className="text-sm font-medium text-textPrimary">
												Detectar Mencao Direta
											</span>
											<p className="text-xs text-grayText">
												Responde quando o numero do bot for mencionado com @
											</p>
										</div>
									</div>
									<div className="relative inline-flex items-center">
										<input
											type="checkbox"
											name="GRUPOS_DETECTAR_MENCAO_DIRETA"
											checked={settings.GRUPOS_DETECTAR_MENCAO_DIRETA !== false}
											onChange={onSettingsChange}
											className="sr-only peer"
										/>
										<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-500/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
									</div>
								</label>

								<label className="flex items-center justify-between p-4 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-violet-500/25 cursor-pointer">
									<div className="flex items-center gap-3">
										<div className="p-2 rounded-full bg-amber-500/20 border border-amber-500/40">
											<MessageSquare className="h-4 w-4 text-amber-300" />
										</div>
										<div>
											<span className="text-sm font-medium text-textPrimary">
												Detectar Palavra-chave
											</span>
											<p className="text-xs text-grayText">
												Responde quando a palavra-chave aparecer na mensagem
											</p>
										</div>
									</div>
									<div className="relative inline-flex items-center">
										<input
											type="checkbox"
											name="GRUPOS_DETECTAR_PALAVRA_CHAVE"
											checked={settings.GRUPOS_DETECTAR_PALAVRA_CHAVE === true}
											onChange={onSettingsChange}
											className="sr-only peer"
										/>
										<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-500/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
									</div>
								</label>

								{settings.GRUPOS_DETECTAR_PALAVRA_CHAVE && (
									<div className="p-4 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-amber-500/25 ml-4">
										<label className="block text-sm font-medium text-textPrimary mb-2">
											Palavra-chave
										</label>
										<input
											type="text"
											name="GRUPOS_PALAVRA_CHAVE_MENCAO"
											value={settings.GRUPOS_PALAVRA_CHAVE_MENCAO || '@bot'}
											onChange={onSettingsChange}
											placeholder="@bot"
											className="w-full rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-white placeholder:text-grayText focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
										/>
										<p className="mt-2 text-xs text-grayText">
											O bot respondera quando esta palavra aparecer na mensagem
										</p>
									</div>
								)}

								<label className="flex items-center justify-between p-4 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-violet-500/25 cursor-pointer">
									<div className="flex items-center gap-3">
										<div>
											<span className="text-sm font-medium text-textPrimary">
												Usar Prompt Diferente para Grupos
											</span>
											<p className="text-xs text-grayText">
												Define um prompt customizado para respostas em grupos
											</p>
										</div>
									</div>
									<div className="relative inline-flex items-center">
										<input
											type="checkbox"
											name="GRUPOS_USAR_PROMPT_DIFERENTE"
											checked={settings.GRUPOS_USAR_PROMPT_DIFERENTE || false}
											onChange={onSettingsChange}
											className="sr-only peer"
										/>
										<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-500/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
									</div>
								</label>

								{settings.GRUPOS_USAR_PROMPT_DIFERENTE && (
									<div className="p-4 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-violet-500/25">
										<label className="block text-sm font-medium text-textPrimary mb-2">
											Prompt Customizado para Grupos
										</label>
										<textarea
											name="GRUPOS_PROMPT_CUSTOMIZADO"
											value={settings.GRUPOS_PROMPT_CUSTOMIZADO || ''}
											onChange={onSettingsChange}
											rows={4}
											placeholder="Digite o prompt especifico para respostas em grupos..."
											className="w-full rounded-lg border border-violet-500/30 bg-violet-500/10 px-3 py-2 text-white placeholder:text-grayText focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500/50 resize-none"
										/>
										<p className="mt-2 text-xs text-grayText">
											Se vazio, usara o prompt principal configurado para a IA selecionada
										</p>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default GroupsSection;
